import React from "react";
import { Helmet } from "react-helmet";
import { Carousel } from "react-responsive-carousel";
import { useParams, useNavigate } from "react-router";
import { useFullscreenMode } from "../../hooks/useFullScreen";
import { exitScreen } from "../../helpers/screen";
import { Loader } from "../../components";
import { routes } from "../../static/routes";
import { PlayerModal } from "./components/playerModal";
import { PrivateErrorModal } from "./components/privateErrorModal";
import { ErrorModal } from "./components/errorModal";
import { getVersion } from "../../helpers/version";
import { getPageTitle } from "../../helpers/envTitle";
import { getCurrentTemplate } from "./helpers/templateUtils";
import { usePlayerData } from "./hooks/usePlayerData";
import { useSlideshow } from "./hooks/useSlideshow";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { SlideRenderer } from "./components/slideRenderer";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import "../dashboard/carousel/style.css";
import "animate.css";

export const Player = () => {
  const navigate = useNavigate();
  const params = useParams();

  // Determine if this is a public or private player
  const isPublic = Boolean(params.userId && params.folderId);
  const isPrivate = Boolean(params.folder && !params.userId);

  const { isFullscreen } = useFullscreenMode();
  const carouselRef = React.useRef();

  const [isStop, setIsStop] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);

  // Data fetching (public + private)
  const {
    state,
    catalogData,
    loading,
    error,
    isPrivateError,
    play,
    setPlay,
    folderItem,
  } = usePlayerData(params, isPublic, isPrivate);

  // Slide index & auto-advance timer
  const { slideIndex, setSlideIndex } = useSlideshow(
    state,
    play,
    catalogData,
    folderItem,
    isPublic,
    carouselRef
  );

  // Close handler
  const handleClose = React.useCallback(async () => {
    exitScreen();
    if (isPrivate && params.folder) {
      await navigate(`/${routes.dashboard}/${params.folder}`);
    } else if (isPublic && params.folderId) {
      await navigate(`/${routes.login}`);
    } else {
      await navigate(`/${routes.dashboard}`);
    }
  }, [isPrivate, isPublic, params.folder, params.folderId, navigate]);

  // Keyboard navigation
  useKeyboardNavigation(
    carouselRef,
    showModal,
    handleClose,
    isStop,
    setIsStop,
    play,
    setPlay,
    setShowModal
  );

  if (loading) {
    return <Loader />;
  }

  const folderId = isPublic ? params.folderId : params.folder;
  const collectionName = isPublic ? catalogData?.name : folderItem?.name;

  return (
    <div className={"carousel__wrapper"} onDoubleClick={handleClose}>
      <Helmet>
        <title>{getPageTitle()}</title>
      </Helmet>
      <div className="carousel__version">
        <div>{getVersion()}</div>
        <div className="carousel__template">
          {getCurrentTemplate(state, slideIndex)}
        </div>
      </div>
      <Carousel
        autoPlay={false}
        infiniteLoop
        ref={carouselRef}
        useKeyboardArrows={true}
        transitionTime={0}
        swipeable={!isPublic}
        showIndicators={false}
        showThumbs={false}
        showStatus={false}
        showArrows={false}
        onChange={(index) => {
          if (!play) {
            setSlideIndex(index);
          }
        }}
        selectedItem={slideIndex}
        key={`carousel-${state.length}`}
      >
        {SlideRenderer({
          state,
          slideIndex,
          catalogData,
          folderItem,
          isPublic,
        })}
      </Carousel>

      {!isFullscreen && !error && !isPrivateError && showModal && (
        <PlayerModal
          handleChangePlay={(newPlay) => {
            setPlay(newPlay);
            setShowModal(false);
            setIsStop(!newPlay);
          }}
          folderId={folderId}
          collectionName={collectionName}
          isPublic={isPublic}
        />
      )}
      {isPrivateError && <PrivateErrorModal />}
      {error && !isPrivateError && <ErrorModal />}
    </div>
  );
};
